import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import OutlinedInput from '@mui/material/OutlinedInput';
import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import type { FilterConfig, ActiveFilter, RowAction } from './types';

// ─── Props ───────────────────────────────────────────────────────────────────

export interface FilterableDataTableProps<T extends { id: number | string }> {
  /** Row data for the current page. */
  rows: T[];
  /** Column definitions for MUI DataGrid. */
  columns: GridColDef[];
  /** Whether data is currently loading. */
  loading: boolean;
  /** Total row count from API (server-side pagination). */
  totalCount: number;

  // Search
  /** Placeholder text for the search input. */
  searchPlaceholder?: string;
  /** Called when the debounced search value changes. */
  onSearchChange?: (value: string) => void;

  // Filters
  /** Filter configuration for the toolbar (select, multiselect, daterange, text). */
  filters?: FilterConfig[];
  /** Called when filters change. Receives the full updated ActiveFilter array. */
  onFilterChange?: (filters: ActiveFilter[]) => void;

  // Pagination (server-side)
  /** Current zero-based page index. */
  page: number;
  /** Number of rows per page. */
  pageSize: number;
  /** Called when the page changes. */
  onPageChange: (page: number) => void;
  /** Called when the page size changes. */
  onPageSizeChange: (size: number) => void;

  // Row interaction
  /** Called when a row is clicked. */
  onRowClick?: (row: T) => void;
  /** Row-level actions rendered in an Actions column menu. */
  actions?: RowAction<T>[];

  // Toolbar extras
  /** Primary action button rendered in the toolbar, right-aligned. */
  primaryAction?: { label: string; onClick: () => void };
}

// ─── Debounce Hook ───────────────────────────────────────────────────────────

/**
 * Custom hook that debounces a value by a given delay (ms).
 */
function useDebouncedValue(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * FilterableDataTable wraps MUI DataGrid with a search/filter toolbar and
 * server-side pagination. It serves as the primary list view component across
 * all pages in the redesigned admin experience.
 *
 * This component owns:
 * - Debounced search input (300ms)
 * - Filter toolbar with select, multiselect, daterange, and text filter types
 * - Server-side pagination (page, pageSize)
 * - Column sorting and resize via DataGrid
 * - Total row count display (never exceeds totalCount from API)
 * - Reset pagination to page 0 when any filter changes
 * - Preserve active filters when page size changes
 * - Primary action button in toolbar (right-aligned)
 * - Row-level action menu with conditional visibility via `show` predicate
 * - Row click support for drill-down navigation
 */
export function FilterableDataTable<T extends { id: number | string }>(
  props: FilterableDataTableProps<T>,
) {
  const {
    rows,
    columns,
    loading,
    totalCount,
    searchPlaceholder = 'Search…',
    onSearchChange,
    filters,
    onFilterChange,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    onRowClick,
    actions,
    primaryAction,
  } = props;

  // ─── Search state ────────────────────────────────────────────────────────
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, 300);

  // Track whether this is the initial render to avoid calling onSearchChange on mount
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    onSearchChange?.(debouncedSearch);
  }, [debouncedSearch, onSearchChange]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  // ─── Filter state ───────────────────────────────────────────────────────
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  /**
   * Updates a filter value and notifies the parent.
   * Resets pagination to page 0 whenever a filter changes (Requirement 13.3).
   */
  const handleFilterChange = useCallback(
    (field: string, value: string | string[] | [string, string]) => {
      setActiveFilters((prev) => {
        // Determine if the new value is "empty" (filter cleared)
        const isEmpty =
          value === '' ||
          (Array.isArray(value) && value.length === 0) ||
          (Array.isArray(value) && value.every((v) => v === ''));

        let updated: ActiveFilter[];
        if (isEmpty) {
          // Remove the filter for this field
          updated = prev.filter((f) => f.field !== field);
        } else {
          // Update or add the filter
          const existing = prev.find((f) => f.field === field);
          if (existing) {
            updated = prev.map((f) => (f.field === field ? { ...f, value } : f));
          } else {
            updated = [...prev, { field, value }];
          }
        }

        // Notify parent with updated filters
        onFilterChange?.(updated);
        // Reset pagination to page 0 when filter changes
        onPageChange(0);

        return updated;
      });
    },
    [onFilterChange, onPageChange],
  );

  // ─── Sorting state ──────────────────────────────────────────────────────
  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const handleSortModelChange = useCallback((model: GridSortModel) => {
    setSortModel(model);
  }, []);

  // ─── Pagination model ───────────────────────────────────────────────────
  const paginationModel: GridPaginationModel = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (model.page !== page) {
        onPageChange(model.page);
      }
      if (model.pageSize !== pageSize) {
        // Preserve active filters when page size changes (Requirement 13.4)
        onPageSizeChange(model.pageSize);
      }
    },
    [page, pageSize, onPageChange, onPageSizeChange],
  );

  // ─── Row click ──────────────────────────────────────────────────────────
  const handleRowClick = useCallback(
    (params: { row: T }) => {
      onRowClick?.(params.row);
    },
    [onRowClick],
  );

  // ─── Row action menu state ──────────────────────────────────────────────
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuRow, setActionMenuRow] = useState<T | null>(null);

  const handleOpenActionMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>, row: T) => {
      event.stopPropagation(); // Prevent row click from firing
      setActionMenuAnchor(event.currentTarget);
      setActionMenuRow(row);
    },
    [],
  );

  const handleCloseActionMenu = useCallback(() => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  }, []);

  const handleActionClick = useCallback(
    (action: RowAction<T>, row: T) => {
      handleCloseActionMenu();
      action.onClick(row);
    },
    [handleCloseActionMenu],
  );

  // ─── Columns with actions ───────────────────────────────────────────────
  /** Appends an Actions column if row actions are provided. */
  const columnsWithActions: GridColDef[] = useMemo(() => {
    if (!actions || actions.length === 0) {
      return columns;
    }

    const actionsColumn: GridColDef = {
      field: '__actions',
      headerName: 'Actions',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row as T;
        // Only show the button if at least one action is visible for this row
        const visibleActions = actions.filter(
          (action) => !action.show || action.show(row),
        );
        if (visibleActions.length === 0) return null;

        return (
          <IconButton
            size="small"
            onClick={(e) => handleOpenActionMenu(e, row)}
            aria-label="Row actions"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        );
      },
    };

    return [...columns, actionsColumn];
  }, [columns, actions, handleOpenActionMenu]);

  // ─── Filter rendering helpers ───────────────────────────────────────────

  /** Gets the current value for a filter field from activeFilters. */
  const getFilterValue = useCallback(
    (field: string): string | string[] | [string, string] => {
      const active = activeFilters.find((f) => f.field === field);
      return active?.value ?? '';
    },
    [activeFilters],
  );

  /** Renders a single select filter dropdown. */
  const renderSelectFilter = useCallback(
    (config: FilterConfig) => {
      const value = getFilterValue(config.field) as string;
      return (
        <FormControl size="small" sx={{ minWidth: 150 }} key={config.field}>
          <InputLabel id={`filter-${config.field}-label`} shrink>{config.label}</InputLabel>
          <Select
            labelId={`filter-${config.field}-label`}
            id={`filter-${config.field}`}
            value={value}
            label={config.label}
            displayEmpty
            notched
            onChange={(event: SelectChangeEvent<string>) => {
              handleFilterChange(config.field, event.target.value);
            }}
            aria-label={`Filter by ${config.label}`}
          >
            <MenuItem value="">All</MenuItem>
            {config.options?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
    [getFilterValue, handleFilterChange],
  );

  /** Renders a multiselect filter with chips. */
  const renderMultiselectFilter = useCallback(
    (config: FilterConfig) => {
      const value = (getFilterValue(config.field) || []) as string[];
      return (
        <FormControl size="small" sx={{ minWidth: 180 }} key={config.field}>
          <InputLabel id={`filter-${config.field}-label`}>{config.label}</InputLabel>
          <Select
            labelId={`filter-${config.field}-label`}
            id={`filter-${config.field}`}
            multiple
            value={value}
            label={config.label}
            onChange={(event: SelectChangeEvent<string[]>) => {
              const newValue = event.target.value;
              // SelectChangeEvent can return string or string[] for multiple
              const arrayValue = typeof newValue === 'string' ? newValue.split(',') : newValue;
              handleFilterChange(config.field, arrayValue);
            }}
            input={<OutlinedInput label={config.label} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((val) => {
                  const opt = config.options?.find((o) => o.value === val);
                  return <Chip key={val} label={opt?.label ?? val} size="small" />;
                })}
              </Box>
            )}
            aria-label={`Filter by ${config.label}`}
          >
            {config.options?.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    },
    [getFilterValue, handleFilterChange],
  );

  /** Renders a daterange filter with from/to text fields. */
  const renderDaterangeFilter = useCallback(
    (config: FilterConfig) => {
      const value = (getFilterValue(config.field) || ['', '']) as [string, string];
      return (
        <Box key={config.field} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label={`${config.label} from`}
            type="date"
            value={value[0] || ''}
            onChange={(e) => {
              handleFilterChange(config.field, [e.target.value, value[1] || '']);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            aria-label={`${config.label} from date`}
          />
          <TextField
            size="small"
            label={`${config.label} to`}
            type="date"
            value={value[1] || ''}
            onChange={(e) => {
              handleFilterChange(config.field, [value[0] || '', e.target.value]);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
            aria-label={`${config.label} to date`}
          />
        </Box>
      );
    },
    [getFilterValue, handleFilterChange],
  );

  /** Renders a text filter input. */
  const renderTextFilter = useCallback(
    (config: FilterConfig) => {
      const value = getFilterValue(config.field) as string;
      return (
        <TextField
          key={config.field}
          size="small"
          label={config.label}
          value={value}
          onChange={(e) => {
            handleFilterChange(config.field, e.target.value);
          }}
          sx={{ minWidth: 150 }}
          inputProps={{ 'aria-label': `Filter by ${config.label}` }}
        />
      );
    },
    [getFilterValue, handleFilterChange],
  );

  /** Renders a single filter based on its type. */
  const renderFilter = useCallback(
    (config: FilterConfig) => {
      switch (config.type) {
        case 'select':
          return renderSelectFilter(config);
        case 'multiselect':
          return renderMultiselectFilter(config);
        case 'daterange':
          return renderDaterangeFilter(config);
        case 'text':
          return renderTextFilter(config);
        default:
          return null;
      }
    },
    [renderSelectFilter, renderMultiselectFilter, renderDaterangeFilter, renderTextFilter],
  );

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Toolbar with search and filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchText}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240, maxWidth: 360 }}
          aria-label={searchPlaceholder}
        />

        {/* Filter controls */}
        {filters?.map((filterConfig) => renderFilter(filterConfig))}

        {/* Spacer to push primary action to the right */}
        {primaryAction && <Box sx={{ flexGrow: 1 }} />}

        {/* Primary action button */}
        {primaryAction && (
          <Button
            variant="contained"
            onClick={primaryAction.onClick}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {primaryAction.label}
          </Button>
        )}
      </Box>

      {/* DataGrid */}
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columnsWithActions}
          loading={loading}
          // Server-side pagination
          paginationMode="server"
          rowCount={totalCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50, 100]}
          // Sorting
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          // Row click
          onRowClick={onRowClick ? handleRowClick : undefined}
          // Behavior
          disableRowSelectionOnClick
          autoHeight
          // Column resize enabled by default in DataGrid
          disableColumnMenu={false}
          sx={{
            border: 'none',
            '& .MuiDataGrid-row': {
              cursor: onRowClick ? 'pointer' : 'default',
            },
          }}
        />
      </Box>

      {/* Row action menu */}
      {actions && actions.length > 0 && (
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={handleCloseActionMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {actionMenuRow &&
            actions
              .filter((action) => !action.show || action.show(actionMenuRow))
              .map((action) => {
                const ActionIcon = action.icon;
                return (
                  <MenuItem
                    key={action.label}
                    onClick={() => handleActionClick(action, actionMenuRow)}
                  >
                    <ListItemIcon>
                      <ActionIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{action.label}</ListItemText>
                  </MenuItem>
                );
              })}
        </Menu>
      )}
    </Box>
  );
}

export default FilterableDataTable;
